import * as React from 'react'
import { useState, useEffect } from 'react'
import clx from 'classnames'

// import { ChevronDownIcon } from '../icons/ChevronDown'
// import { CheckIcon } from '../icons/Check'
import { useClickOutside } from '@/app/hooks/useClickOutside'

import { YearMonthPickerButton } from './YearMonthPickerButton'

type YearMonth = {
  year: number
  month: number // 1–12
}

export function parseYearMonth(input?: string | null): YearMonth | null {
  if (!input) return null

  const s = String(input).trim()
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(s)
  if (m) {
    const year = Number(m[1])
    const month = Number(m[2])
    if (year >= 1 && month >= 1 && month <= 12) return { year, month }
  }

  const date = new Date(s)
  if (Number.isNaN(date.getTime())) return null

  return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

export type Option = {
  key?: string | number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type Props = {
  label?: string
  placeholder?: string
  icon?: React.ElementType
  className?: string
  value?: string | number | null
  error?: string | boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
  handleChange?: (value: string) => void
  name?: string
  disabled?: boolean
  children?: React.ReactNode
  required?: boolean
  overflow?: boolean
}

export const FieldsetMonthYearPicker = ({
  label,
  value,
  name,
  overflow = false,
  placeholder = 'Wybierz...',
  icon: Icon = null,
  disabled,
  handleChange,
  onChange,
  error,
  className,
  required,
  children,
}: Props) => {
  const [storedValue, setStoredValue] = useState<string | number>(value)
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [searchQuery, setSearchQuery] = useState<string>('')

  useEffect(() => {
    if (value !== undefined && value !== storedValue) {
      setStoredValue(value)
    }
  }, [value])

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  useClickOutside({
    ref: dropdownRef,
    handler: () => {
      setOpen(false)
      setSearchQuery('')
    },
  })

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as HTMLElement
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  const parsedValue = value ? parseYearMonth(String(value)) : null

  const handleSelect = (value: YearMonth) => {
    if (value.month) {
      const formattedValue = `${value.year}-${String(value.month).padStart(2, '0')}`
      handleChange?.(formattedValue)
    }
  }

  return (
    <div>
      <input
        type="hidden"
        required={required}
        name={name}
        value={storedValue}
        readOnly
      />
      {label && (
        <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}
      <div
        className={clx('tw-relative tw-w-full', className)}
        ref={dropdownRef}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="dropdown-list"
      >
        <YearMonthPickerButton
          clearable={false}
          value={parsedValue || undefined}
          handleChange={handleSelect}
        />
      </div>
      {error && typeof error === 'string' && (
        <span className="tw-group-focus:tw-opacity-0 tw-text-xs tw-font-medium tw-text-error">
          {error}
        </span>
      )}
      <div>{children}</div>
    </div>
  )
}
